import React from "react"

import FormControl, { FormControlProps } from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormHelperText from "@mui/material/FormHelperText"
import Grid from "@mui/material/Grid"
import Radio, { RadioProps } from "@mui/material/Radio"
import MuiRadioGroup, {
    RadioGroupProps as MuiRadioGroupProps
} from "@mui/material/RadioGroup"
import Typography from "@mui/material/Typography"
import { alpha, styled } from "@mui/material/styles"

export interface RadioGroupOption<T> {
    value: T
    label: string
}

interface RadioGroupProps<T> {
    options: RadioGroupOption<T>[]
    label?: JSX.Element | string
    description?: string
    onChange: MuiRadioGroupProps["onChange"]
    name: string
    value: T
    helperText?: string
    showHeader?: boolean
    showDescription?: boolean
    FormControlProps?: FormControlProps<"fieldset">
    FormControlLabelProps?: any
    RadioProps?: RadioProps
    points?: boolean
    direction?: "row" | "column"
    style?: React.CSSProperties
}

const CalculatorRadioGroup: React.FC<RadioGroupProps<any>> = props => {
    const {
        options,
        name,
        value,
        onChange,
        helperText,
        style,
        points,
        direction,
        showHeader = true,
        showDescription = true,
        description,
        label
    } = props

    return (
        <FormControl
            component="fieldset"
            fullWidth
            {...props.FormControlProps}>
            {showHeader ? (
                <Grid
                    item
                    container
                    xs
                    alignItems="stretch">
                    {typeof label === "string" ? (
                        <Typography variant="body2">{label}</Typography>
                    ) : (
                        label
                    )}
                </Grid>
            ) : null}
            {showDescription ? (
                <StyledTypography1
                    variant="h5"
                    component="label">
                    {description}
                </StyledTypography1>
            ) : null}
            <MuiRadioGroup
                aria-label={label as string}
                name={name}
                value={value ?? " "}
                onChange={onChange}>
                <Grid
                    container
                    direction={direction}
                    style={style}>
                    {options.map(option => (
                        <StyledDiv
                            checked={value === option.value}
                            key={option.label + option.value}>
                            <StyledFormControlLabel
                                {...props.FormControlLabelProps}
                                points={points}
                                value={option.value}
                                control={<StyledRadio {...props.RadioProps} />}
                                label={
                                    points ? (
                                        <Grid
                                            container
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            wrap="nowrap">
                                            <Typography
                                                component="p"
                                                variant="body2">
                                                {option.label}
                                            </Typography>
                                            <StyledTypography2 component="p">
                                                {option.value}
                                            </StyledTypography2>
                                        </Grid>
                                    ) : (
                                        <Typography variant="body2">
                                            {option.label}
                                        </Typography>
                                    )
                                }
                            />
                        </StyledDiv>
                    ))}
                </Grid>
            </MuiRadioGroup>
            {helperText ? (
                <StyledFormHelperText>{helperText}</StyledFormHelperText>
            ) : null}
        </FormControl>
    )
}

export default CalculatorRadioGroup

const StyledTypography1 = styled(Typography)<{ component: string }>(
    ({ theme }) => ({
        marginBottom: theme.spacing(0.5)
    })
)

const StyledTypography2 = styled(Typography)<{ component: string }>(
    ({ theme }) => ({
        padding: theme.spacing(0.5)
    })
)

const StyledRadio = styled(Radio)(({ theme }) => ({
    "&.MuiRadio-root": {
        "&:hover": {
            backgroundColor: alpha(theme.palette.secondary.main, 0)
        }
    },
    "&.Mui-checked": {
        "&$checked": {
            "&:hover": {
                backgroundColor: alpha(theme.palette.secondary.main, 0)
            }
        }
    }
}))

const StyledFormControlLabel = styled(FormControlLabel)<{ points: boolean }>(
    ({ points, theme }) => ({
        ...(points && {
            "&.MuiFormControlLabel-root": {
                display: "flex",
                marginRight: theme.spacing(0.5)
            },
            "&.MuiFormControlLabel-label": {
                flexGrow: 1
            }
        }),

        ...(!points && {
            "&.MuiFormControlLabel-root": {
                width: "100%"
            },
            "&.MuiFormControlLabel-label": {
                width: "100%"
            }
        })
    })
)

const StyledDiv = styled("div")<{ checked: boolean }>(({ theme, checked }) => ({
    ...(checked && {
        backgroundColor: alpha(theme.palette.secondary.main, 0.04)
    }),
    "&:hover": {
        backgroundColor: alpha(theme.palette.secondary.main, 0.04)
    }
}))

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
    "&.Mui-disabled": {
        color: theme.palette.text.primary,
        opacity: 1
    }
}))
